import React, { useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { searchSoldCardPrices } from '../api/cards';
import type { ChartPriceTransaction } from '../types';

interface PriceChartProps {
  idLabel?: string;
  selectedPlatform?: string;
}

// 平台颜色映射
const platformColors: Record<string, string> = {
  ebay: '#0064D2',
  snkrdunk: '#1a1a1a',
  mercari: '#FF6B00',
  煤炉: '#FF6B00',
  default: '#666666',
};

type TimeRange = 'all' | '1y' | '6m' | '3m' | '1m';

const timeRangeOptions: { key: TimeRange; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: '1y', label: '近一年' },
  { key: '6m', label: '近半年' },
  { key: '3m', label: '三个月' },
  { key: '1m', label: '一个月' },
];

export default function PriceChart({ idLabel, selectedPlatform }: PriceChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('3m');
  const [chartTransactions, setChartTransactions] = useState<ChartPriceTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchChartData = async () => {
      if (!idLabel) {
        setChartTransactions([]);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const response = await searchSoldCardPrices(idLabel, timeRange);
        if (response.code === 200) {
          setChartTransactions(response.data || []);
        } else {
          setChartTransactions([]);
          setError(response.message || '获取图表数据失败');
        }
      } catch (err: any) {
        setChartTransactions([]);
        setError(err?.response?.data?.message || '获取图表数据失败');
      } finally {
        setIsLoading(false);
      }
    };

    fetchChartData();
  }, [idLabel, timeRange]);

  const { chartOption, hasRangeData } = useMemo(() => {
    const validData = chartTransactions.filter(
      (t): t is ChartPriceTransaction & { price: number; soldTime: number } =>
        typeof t.price === 'number' && typeof t.soldTime === 'number'
    );

    const filteredData = selectedPlatform
      ? validData.filter((t) => t.platform === selectedPlatform)
      : validData;

    const sortedData = [...filteredData].sort((a, b) => a.soldTime - b.soldTime);
    const currency = sortedData[0]?.currency || '';
    const allPriceData = sortedData.map((t) => ({
      time: t.soldTime,
      price: t.price,
    }));

    // 找出最高价和最低价点
    let maxPoint: { time: number; price: number } | null = null;
    let minPoint: { time: number; price: number } | null = null;
    
    allPriceData.forEach((d) => {
      if (!maxPoint || d.price > maxPoint.price) {
        maxPoint = d;
      }
      if (!minPoint || d.price < minPoint.price) {
        minPoint = d;
      }
    });

    // 按平台分组
    const platformData: Record<string, { time: number; price: number }[]> = {};
    sortedData.forEach((t) => {
      if (!platformData[t.platform]) {
        platformData[t.platform] = [];
      }
      platformData[t.platform].push({
        time: t.soldTime,
        price: t.price,
      });
    });

    // 构建价格系列数据
    const priceSeries = Object.entries(platformData).map(([platform, data]) => ({
      name: platform,
      type: 'line' as const,
      smooth: true,
      showSymbol: false,
      sampling: 'lttb' as const,
      yAxisIndex: 0,
      lineStyle: {
        width: 2,
        color: platformColors[platform] || platformColors.default,
      },
      itemStyle: {
        color: platformColors[platform] || platformColors.default,
      },
      data: data.map((d) => [d.time, d.price]),
      // 高低点标注
      markPoint: {
        symbol: 'pin',
        symbolSize: 40,
        data: [
          ...(maxPoint && data.some((d) => d.time === maxPoint!.time && d.price === maxPoint!.price)
            ? [
                {
                  name: '最高',
                  coord: [maxPoint.time, maxPoint.price],
                  value: maxPoint.price,
                  itemStyle: { color: '#ef4444' },
                  label: {
                    formatter: `{c}`,
                    color: '#fff',
                    fontSize: 10,
                  },
                },
              ]
            : []),
          ...(minPoint && data.some((d) => d.time === minPoint!.time && d.price === minPoint!.price)
            ? [
                {
                  name: '最低',
                  coord: [minPoint.time, minPoint.price],
                  value: minPoint.price,
                  itemStyle: { color: '#22c55e' },
                  label: {
                    formatter: `{c}`,
                    color: '#fff',
                    fontSize: 10,
                  },
                },
              ]
            : []),
        ],
      },
    }));

    const option = {
      tooltip: {
        trigger: 'axis' as const,
        backgroundColor: '#fff',
        borderColor: '#e5e5e5',
        borderWidth: 1,
        textStyle: {
          color: '#1a1a1a',
        },
        formatter: (params: any) => {
          if (!params || params.length === 0) return '';
          const date = new Date(params[0].value[0]);
          const dateStr = date.toLocaleDateString('zh-CN');
          let result = `<div style="font-weight: 500; margin-bottom: 4px;">${dateStr}</div>`;
          params.forEach((p: any) => {
            result += `<div style="display: flex; align-items: center; gap: 8px;">
              <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${p.color};"></span>
              <span>${p.seriesName}: ${p.value[1]} ${currency}</span>
            </div>`;
          });
          return result;
        },
      },
      legend: {
        data: Object.keys(platformData),
        bottom: 0,
        textStyle: {
          color: '#666',
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '18%',
        top: '10%',
        containLabel: true,
      },
      xAxis: {
        type: 'time' as const,
        axisLine: {
          lineStyle: {
            color: '#e5e5e5',
          },
        },
        axisLabel: {
          color: '#666',
          formatter: (value: number) => {
            const date = new Date(value);
            return `${date.getMonth() + 1}/${date.getDate()}`;
          },
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: 'value' as const,
        name: '价格',
        position: 'left' as const,
        axisLine: {
          show: false,
        },
        axisLabel: {
          color: '#666',
        },
        splitLine: {
          lineStyle: {
            color: '#f0f0f0',
          },
        },
      },
      series: priceSeries,
    };

    return { chartOption: option, hasRangeData: allPriceData.length > 0 };
  }, [chartTransactions, selectedPlatform]);

  return (
    <div className="bg-white rounded-lg border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-base font-medium text-primary">价格走势</h3>
        <div className="flex items-center gap-2 flex-wrap">
          {timeRangeOptions.map((option) => (
            <button
              key={option.key}
              onClick={() => setTimeRange(option.key)}
              className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                timeRange === option.key
                  ? 'bg-primary text-white'
                  : 'bg-white border border-border text-secondary hover:border-primary'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="h-64 flex items-center justify-center">
          <p className="text-secondary">图表数据加载中...</p>
        </div>
      ) : error ? (
        <div className="h-64 flex items-center justify-center">
          <p className="text-red-500">{error}</p>
        </div>
      ) : hasRangeData ? (
        <ReactECharts option={chartOption} notMerge={true} style={{ height: 320 }} />
      ) : (
        <div className="h-64 flex items-center justify-center">
          <p className="text-secondary">当前时间范围暂无价格数据</p>
        </div>
      )}
    </div>
  );
}
